"use client"

import { motion } from 'framer-motion'
import { MapPin, Navigation, CalendarPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'

type Location = {
  name: string
  address: string
  coordinates?: string // Format: "lat,lng"
  placeId?: string // Google Place ID
}

const getMapboxStaticImageUrl = (coordinates: string, width: number = 1200, height: number = 200) => {
  const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
  
  const env = import.meta.env as { VITE_MAPBOX_TOKEN?: string; VITE_MAPBOX_ACCESS_TOKEN?: string }
  const accessToken = env.VITE_MAPBOX_TOKEN || env.VITE_MAPBOX_ACCESS_TOKEN
  
  if (!accessToken) {
    console.warn('Mapbox token not found for static images')
    return ''
  }
  
  const styleId = 'streets-v12'
  const username = 'mapbox'
  const zoom = 13.5
  const pinColor = 'ff3333'
  const marker = `pin-l+${pinColor}(${lng},${lat})`
  
  return `https://api.mapbox.com/styles/v1/${username}/${styleId}/static/${marker}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${accessToken}`
}

const openDirections = (address: string, coordinates?: string, placeId?: string) => {
  const encodedAddress = encodeURIComponent(address)
  
  let coords = ''
  if (coordinates) {
    const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
    coords = `${lat},${lng}`
  }
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  let mapUrl: string
  let googleMapsUrl: string
  
  if (isIOS) {
    // Use coordinates for Apple Maps (more reliable than address)
    mapUrl = coords 
      ? `maps://maps.apple.com/?daddr=${coords}&dirflg=d`
      : `maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`
    window.location.href = mapUrl
    
    // Build Google Maps fallback URL
    if (placeId) {
      // Use place URL format which opens the business listing page
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else {
      googleMapsUrl = coords 
        ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
    }
    
    setTimeout(() => {
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  } else {
    // Build Google Maps URL for non-iOS devices
    if (placeId) {
      // Use place URL format which opens the business listing page
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else {
      googleMapsUrl = coords
        ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
    }
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
  }
}

type SimpleLocationsSectionProps = {
  locations?: Location[]
  onBook?: (locationName: string) => void
}

const defaultLocations: Location[] = [
  {
    name: 'Noosa',
    address: '31 Burgess Drive, Tewantin QLD 4565',
    coordinates: '-26.406336265416424, 153.0262562704724', // Updated Noosa clinic coordinates
    placeId: 'ChIJEWpOLsNpk2sRInDiJFxvysY', // Google Place ID for Raintree Chinese Medicine & Acupuncture Noosa
  },
  {
    name: 'Maroochydore',
    address: 'Ground Level 5/55 Plaza Parade, Maroochydore QLD 4558',
    coordinates: '-26.657529591547217, 153.08437335189674',
  },
]

const getLocationDisplayName = (locationName: string): string => {
  if (locationName === 'Noosa') {
    return 'Noosa Clinic'
  }
  if (locationName === 'Maroochydore') {
    return 'Maroochydore Clinic'
  }
  return locationName
}

const getClinicDescription = (locationName: string): string => {
  if (locationName === 'Noosa') {
    return 'Raintree Chinese Medicine, Noosa, Sunshine Coast'
  }
  if (locationName === 'Maroochydore') {
    return 'Raintree Chinese Medicine, Maroochydore, Sunshine Coast'
  }
  return ''
}

export function SimpleLocationsSection({ locations = defaultLocations, onBook }: SimpleLocationsSectionProps) {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141]">Appointments, Locations & How to Start</h2>
          <p className="text-gray-600 max-w-2xl mx-auto px-8 lg:px-0">
            Fertility and preconception appointments are offered at both clinics:
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {locations.map((location, index) => (
            <motion.div
              key={location.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm flex flex-col"
            >
              {/* Map thumbnail */}
              <div className="w-full h-32 overflow-hidden relative bg-gray-200">
                {location.coordinates ? (
                  <img
                    src={getMapboxStaticImageUrl(location.coordinates, 1200, 200)}
                    alt={`Map showing location of ${location.name}`}
                    className="w-full h-full object-cover"
                    style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <MapPin className="h-6 w-6" />
                  </div>
                )}
                {/* Get Directions button */}
                <button
                  onClick={() => openDirections(location.address, location.coordinates, location.placeId)}
                  className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs font-medium shadow-lg flex items-center gap-1 transition-colors z-10"
                  aria-label={`Get directions to ${location.name}`}
                >
                  <Navigation className="h-3 w-3" aria-hidden="true" />
                  <span>Directions</span>
                </button>
              </div>
              <div className="p-4 flex flex-col flex-grow">
                <div className="flex items-start gap-2 mb-3">
                  <MapPin className="h-5 w-5 text-[#a48b61] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-lg font-normal text-[#3f4141] mb-1">{getLocationDisplayName(location.name)}</h3>
                    {getClinicDescription(location.name) && (
                      <p className="text-gray-600 text-sm mb-1">{getClinicDescription(location.name)}</p>
                    )}
                    <p className="text-gray-600 text-sm">{location.address}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => onBook?.(location.name)}
                  className="w-full border-gray-300 text-gray-700 bg-white hover:bg-gray-50 mt-auto"
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Appointment - {getLocationDisplayName(location.name)}
                  </span>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

